import { createFileRoute } from '@tanstack/react-router';
import { CaseStudyLayout, type CaseStudyContent } from '../components/CaseStudyLayout.js';
import { BookingMockup } from '../components/visuals/BookingMockup.js';
import { IconClinic } from '../components/visuals/IndustryIcons.js';
import { usePageTitle } from '../components/usePageTitle.js';

export const Route = createFileRoute('/for-mortgage-brokers')({
  component: MortgageBrokersPage,
});

function MortgageBrokersPage() {
  usePageTitle('Bookings for mortgage brokers');
  return <CaseStudyLayout content={content} />;
}

const content: CaseStudyContent = {
  eyebrow: 'For mortgage brokers',
  icon: IconClinic,
  headline: 'Pre-approvals when leads are hot. Reviews when rates move.',
  headlineEmphasis: 'rates move',
  subhead:
    'Bookaburra books pre-approval meetings 24/7, blocks out settlement days, and reminds you when each client is due for their annual rate review — before they ring another broker.',
  Visual: BookingMockup,
  business: {
    name: 'Northern Beaches Mortgage Co.',
    location: 'Sydney Northern Beaches · 2 brokers · 1 meeting room + home visits',
    summary:
      "A fictional 2-broker boutique on the Northern Beaches. Most pre-approval meetings happen at the office; settlement-day check-ins happen at the client's home or workplace. Annual rate reviews are quarterly through the year. Leads land at all hours — Saturday morning is the busiest enquiry window.",
  },
  dayTimeline: [
    {
      time: '6:45',
      event:
        'A fresh enquiry lands overnight — Saturday morning auction-shopper books a pre-approval for Monday 9am.',
      bookaburraResponse:
        '24/7 self-serve. The slot was offered against the office calendar and confirmed without anyone touching it.',
    },
    {
      time: '9:00',
      event:
        'Monday morning pre-approval starts. Confirmation email had the required-docs checklist attached for that service type.',
      bookaburraResponse:
        'Per-service prep instructions render from the saved template. Client arrives with payslips and ID in hand.',
    },
    {
      time: '10:30',
      event:
        'Broker drives to a client’s home for a doc-collection meeting (60 min service).',
      bookaburraResponse:
        'On-site service added a travel buffer to the broker’s calendar. The next slot offered is back at the office no earlier than 12:00.',
    },
    {
      time: '11:45',
      event:
        'A client tries to add another meeting on his settlement day from the public page.',
      bookaburraResponse:
        'Settlement day is blocked off the booking calendar — no slots are offered against it. Picker shows Thursday morning instead.',
    },
    {
      time: '13:30',
      event:
        'Follow-up list surfaces 8 clients whose 12-month rate review is due in the next fortnight.',
      bookaburraResponse:
        'One batch of magic-link "book your annual review" emails goes out. Three clients self-serve into next week’s diary by close of business.',
    },
    {
      time: '15:00',
      event:
        'Bank delays a settlement by two days. The client opens his confirmation email and clicks reschedule.',
      bookaburraResponse:
        'Magic-link reschedule lets him pick a new slot that respects the broker’s availability. Calendar updates without a phone call.',
    },
  ],
  pains: [
    {
      quote:
        'A fresh lead emailed at 9pm asking if we could meet this week. By the time we replied the next morning, they’d booked someone else.',
      explanation:
        'Hot leads do not wait for office hours. If they cannot book themselves, the next broker gets the call.',
    },
    {
      quote:
        'A client rang to complain their rate was no longer competitive. Their twelve-month review had quietly slipped past us.',
      explanation:
        'Annual reviews are recurring work. The calendar should surface them before the client notices.',
    },
    {
      quote:
        'A settlement day ended up with another meeting booked on top of it. Settlements need two or three uninterrupted hours.',
      explanation:
        'Some days belong to one client. The booking page has to respect that.',
    },
  ],
  solutions: [
    {
      title: '24/7 self-serve booking',
      body: (
        <>
          Leads book their own pre-approval meeting at 9pm on a Saturday. You
          wake up to a calendar full of qualified appointments, not a queue of
          callbacks.
        </>
      ),
    },
    {
      title: 'Follow-up list for annual reviews',
      body: (
        <>
          Each service has a recommended frequency. Bookaburra surfaces the
          clients whose twelve-month rate review is due, so you can reach out
          before they shop around.
        </>
      ),
    },
    {
      title: 'Closed days and blocked time',
      body: (
        <>
          Mark settlement days off the booking calendar. Nothing else lands on
          them — the client who matters today gets your full attention.
        </>
      ),
    },
    {
      title: 'Magic-link reschedule',
      body: (
        <>
          When the bank delays, the client moves their own slot from the email
          you already sent. No phone tag, no double entry.
        </>
      ),
    },
  ],
  industrySlug: 'mortgage-brokers',
};
